import { useState, useCallback } from 'react';
import { Message } from '../AiChat/types';

const STORAGE_KEY = 'command-bar-chat-sessions';
const MAX_SESSIONS = 10;

export type ChatSession = {
  id: string;
  title: string;
  messages: Message[];
  updatedAt: number;
};

function loadSessions(): ChatSession[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    return JSON.parse(raw);
  } catch {
    return [];
  }
}

function persistSessions(sessions: ChatSession[]): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(sessions));
  } catch {
    // storage full or unavailable
  }
}

function extractTitle(messages: Message[]): string {
  const first = messages.find((m) => m.role === 'user');
  if (!first) return 'Chat';
  const text = first.content.trim();
  return text.length > 60 ? text.slice(0, 57) + '...' : text;
}

export function useChatSessions(): {
  sessions: ChatSession[];
  saveSession: (sessionId: string | null, messages: Message[]) => string;
  removeSession: (sessionId: string) => void;
  clearAllSessions: () => void;
  getSession: (sessionId: string) => ChatSession | undefined;
} {
  const [sessions, setSessions] = useState<ChatSession[]>(loadSessions);

  const saveSession = useCallback(
    (sessionId: string | null, messages: Message[]): string => {
      if (messages.length === 0) return sessionId ?? '';

      const id =
        sessionId ??
        `session_${Date.now()}_${Math.random().toString(36).slice(2)}`;
      const title = extractTitle(messages);
      const newSession: ChatSession = {
        id,
        title,
        messages,
        updatedAt: Date.now(),
      };

      // Read current sessions from localStorage directly to avoid stale state
      const current = loadSessions();
      const filtered = current.filter((s) => s.id !== id);
      const updated = [newSession, ...filtered].slice(0, MAX_SESSIONS);
      persistSessions(updated);
      setSessions(updated);

      return id;
    },
    [],
  );

  const removeSession = useCallback((sessionId: string): void => {
    const current = loadSessions();
    const updated = current.filter((s) => s.id !== sessionId);
    persistSessions(updated);
    setSessions(updated);
  }, []);

  const clearAllSessions = useCallback((): void => {
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch {
      // storage unavailable
    }
    setSessions([]);
  }, []);

  const getSession = useCallback(
    (sessionId: string): ChatSession | undefined => {
      // Read from localStorage directly for freshness
      const current = loadSessions();
      return current.find((s) => s.id === sessionId);
    },
    [],
  );

  return {
    sessions,
    saveSession,
    removeSession,
    clearAllSessions,
    getSession,
  };
}
